import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission } from '../../dux';
import {
  BillActivity,
  HearingActivity,
  UserActivity,
  VoteActivity,
} from '../Shared/ActivityFeed/Activity';
import ActivityFeed from '../Shared/ActivityFeed/ActivityFeed';
import { FILTER_OPTIONS, Filter } from '../Shared/ActivityFeed/ActivityFeedHelper';
import {
  isJurisdictionSupportedForAISummary,
  AISummaryType,
} from '../Shared/BillInsights/utils';

type BillActivityFeedProps = {
  bill: BillDetailed;
  userActions: UserAction[];
  orgUser?: OrganizationUser;
  billVersionDisplayed?: BillVersion;
  selectedBillVersion?: number;
  setSelectedBillVersion: (version: number) => void;
};

const BillActivityFeed = (props: BillActivityFeedProps): ReactElement => {
  const {
    bill,
    userActions,
    orgUser,
    billVersionDisplayed,
    selectedBillVersion,
    setSelectedBillVersion,
  } = props;

  const dispatch = useDispatch();
  const canView = () => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.HEARINGS, true),
    );
  };

  const canComment = () => {
    return dispatch(
      checkPermission(abilityGlossary.CREATE, abilityGlossary.BILL_ANNOTATION, true),
    );
  };

  const canViewPositionStatement = () => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.BILL_POSITION, true),
    );
  };

  const hasStatement =
    !!bill.positionStatements &&
    bill.positionStatements.length > 0 &&
    Object.keys(bill.positionStatements[0]).length > 0;

  const supportedJurisdiction = isJurisdictionSupportedForAISummary(
    bill.session.state.abbreviation,
    AISummaryType.IntroducedVersionSummary,
  );

  const removeRestrictedOptions = (options: typeof FILTER_OPTIONS) => {
    return options.filter(
      (option) =>
        option.value !== Filter.NOTES &&
        option.value !== Filter.ARCHIVED &&
        option.value !== Filter.FILES &&
        option.value !== Filter.STARRED,
    );
  };

  const disableHearings = (options: typeof FILTER_OPTIONS) => {
    return options.map((option) => {
      return option.value === Filter.HEARINGS
        ? { ...option, isDisabled: true }
        : option;
    });
  };

  const filterOptions = (() => {
    if (!canComment()) {
      let options = removeRestrictedOptions(FILTER_OPTIONS);
      if (!canView()) {
        options = disableHearings(options);
      }
      return options;
    } else {
      return FILTER_OPTIONS;
    }
  })();

  const activities = BillActivity.transformBillActions(bill.actions)
    .concat(
      orgUser
        ? UserActivity.transformUserActions(
            userActions,
            orgUser,
            setSelectedBillVersion,
            selectedBillVersion,
          )
        : [],
    )
    .concat(
      bill.hearings && !!canView()
        ? HearingActivity.transformHearings(bill.hearings)
        : [],
    )
    .concat(bill.votes ? VoteActivity.transformVotes(bill.votes) : [])
    .concat(
      canViewPositionStatement() && hasStatement && bill.positionStatements
        ? UserActivity.transformPositionStatement(bill.positionStatements[0])
        : [],
    );

  return (
    <ActivityFeed
      activities={activities}
      aiSummary={supportedJurisdiction}
      bill={bill}
      billVersionDisplayed={billVersionDisplayed}
      filterOptions={filterOptions}
    />
  );
};

export default BillActivityFeed;
